import { useCallback, useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import BASE_DEV_HOST from 'common/config/api';
import { getStoredAccessToken } from 'pages/Auth/utils';
import { formatLastUsed } from 'utilities';

interface ConnectedApp {
  id: string;
  client_name: string;
  token_preview: string;
  scope: string;
  created_at: string;
  last_used_at: string | null;
  is_active: boolean;
}

const QUERY_KEY = ['mcp-tokens'];

async function fetchConnectedApps(): Promise<ConnectedApp[]> {
  const accessToken = getStoredAccessToken();
  if (!accessToken) return [];

  const res = await fetch(`${BASE_DEV_HOST}/auth/mcp/tokens`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) return [];
  return (await res.json()) as ConnectedApp[];
}

async function revokeApp(tokenId: string): Promise<void> {
  const accessToken = getStoredAccessToken();
  if (!accessToken) throw new Error('Not authenticated');

  const res = await fetch(`${BASE_DEV_HOST}/auth/mcp/tokens/${tokenId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok && res.status !== 204) {
    throw new Error('Failed to revoke token');
  }
}

const ConnectedApps = () => {
  const queryClient = useQueryClient();
  const [revokeTarget, setRevokeTarget] = useState<ConnectedApp | null>(null);

  const { data: apps = [], isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchConnectedApps,
    staleTime: 30_000,
  });

  const revokeMutation = useMutation({
    mutationFn: (tokenId: string) => revokeApp(tokenId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setRevokeTarget(null);
    },
  });

  const handleRevoke = useCallback(() => {
    if (revokeTarget) {
      revokeMutation.mutate(revokeTarget.id);
    }
  }, [revokeTarget, revokeMutation]);

  if (isLoading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Connected Apps
        </Typography>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Connected Apps
      </Typography>

      {apps.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No connected apps. Connect an MCP-compatible application to see it here.
        </Typography>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 1 }}>
          <List disablePadding>
            {apps.map((app, index) => (
              <ListItem
                key={app.id}
                divider={index < apps.length - 1}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="revoke"
                    onClick={() => setRevokeTarget(app)}
                    data-testid={`revoke-token-${app.id}`}>
                    <DeleteOutlineIcon />
                  </IconButton>
                }>
                <ListItemText
                  primary={app.client_name}
                  secondary={`Last used: ${formatLastUsed(app.last_used_at)}`}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <Dialog open={!!revokeTarget} onClose={() => setRevokeTarget(null)}>
        <DialogTitle>Revoke Access</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to revoke access for {revokeTarget?.client_name}? This will
            disconnect the application and it will need to be re-authorized.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)} disabled={revokeMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleRevoke}
            color="error"
            disabled={revokeMutation.isPending}
            data-testid="confirm-revoke-btn">
            {revokeMutation.isPending ? 'Revoking...' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConnectedApps;
